import React, { PropsWithChildren } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import styles from './DrawerContainer.module.scss';
import { useReadableEventRefresher } from '@zdrbm/zdr-react';
import { IDrawerController } from '../DrawerController';
import { DrawerContextProvider } from '../DrawerContextProvider';
import { SidePanelHeader } from 'features/pi/view/drawer/side-panel-drawer/side-panel-header/side-panel-header';
import { UncaughtExceptionComponent } from 'features/pi/view/drawer/side-panel-drawer/unexpected-exception/unexpected-exception';
import { Divider } from '@placer-ui/components';
import { IAssistantUiManager } from 'features/pi/view/uiManager/AssistantUiManager';
import { setElevioLauncherVisible } from 'utils/elevio/elevio';
import { PiError } from 'features/pi/model/pi-errors/PiError';

interface DrawerContainerProps {
    drawerContent: React.ReactNode;
    drawerSize: number;
    drawerController: IDrawerController;
    drawerUiManager: IAssistantUiManager;
}

export const DrawerContainer = ({
    children,
    drawerContent,
    drawerSize,
    drawerUiManager,
    drawerController,
}: PropsWithChildren<DrawerContainerProps>) => {
    const [[isOpen]] = useReadableEventRefresher(drawerController.isOpen);
    const drawerSizeInPx = `${drawerSize}px`;

    const onError = (error: Error) => {
        console.error(new PiError('PI ErrorBoundary', { cause: error }));
    };

    return (
        <DrawerContextProvider drawerController={drawerController}>
            <div
                className={styles.drawerContainer}
                style={
                    {
                        '--right-offset': isOpen ? drawerSizeInPx : '0px',
                    } as React.CSSProperties
                }
            >
                <div
                    id="drawer-controller"
                    className={styles.childrenContent}
                    style={{
                        width: isOpen ? `calc(100% - ${drawerSizeInPx})` : '100%',
                    }}
                >
                    {children}
                </div>
                {isOpen && (
                    <div
                        data-testid="pi-drawer"
                        className={styles.drawerContent}
                        style={{
                            width: drawerSizeInPx,
                        }}
                        data-drawer-open={isOpen}
                    >
                        <ErrorBoundary
                            onError={onError}
                            FallbackComponent={props => (
                                <UncaughtExceptionComponent
                                    drawerSize={drawerSize}
                                    {...props}
                                />
                            )}>
                            <SidePanelHeader
                                title={'Pi Assist'}
                                data-testid="drawer-header"
                                onClickNewChat={() => drawerUiManager.createNewChat()}
                                onClickHistory={() => drawerUiManager.toggleHistory()}
                                onClose={() => {
                                    drawerUiManager.closeAssistantPanel();
                                    setElevioLauncherVisible(true);
                                }}
                            />
                            <Divider />
                            {drawerContent}
                        </ErrorBoundary>

                    </div>
                )}
            </div>
        </DrawerContextProvider>
    );
};

export default DrawerContainer;
